import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";

import { setRosterStar } from "@/queries/api/dashboardV2Api";
import type { RosterItem } from "@/queries/api/dashboardV2Api";
import { getShifts } from "@/queries/api/shiftApi";
import { OfferDaysPicker } from "@/components/shift-detail/offer-days-picker";
import { getShiftDetail } from "@/queries/api/employerDashboardApi";
import { useAddUpdateOfferMutation } from "@/queries/hooks/businessShiftHooks";
import {
  useSearchShiftsMutation,
  useSearchWorkersMutation,
} from "@/queries/hooks/searchHooks";
import { QueryKeys } from "@/queries/queryKeys";
import type { Profile } from "@/schemas/profile";

import {
  ROSTER_SORT_DEFAULT_DIR,
  RosterItemsTable,
} from "../dashboard/-components/v2/roster-table";
import type { RosterSortKey } from "../dashboard/-components/v2/roster-table";
import { useRole } from "@/utils/hooks/use-role";
import type { Role } from "@/utils/hooks/use-role";
import { Logger } from "@/utils/logger";
import notify from "@/utils/notify";

import { FindShiftPanel } from "./-components/find-shift-panel";
import { ShiftDetails } from "./-components/shift-details";
import ShiftMap from "./-components/shift-map";
import { UserDetails } from "./-components/user-details";
import { BottomViewToggle } from "./-components/view-toggle";
import {
  computeCenterFromShifts,
  coerceCenter,
  buildSearchPayload,
  getRoleLabelFromValue,
  mapApiShiftToUIShift,
  performSearch,
  EMPLOYER_SEARCH_RADIUS_MILES,
} from "./-helpers";

import type { Shift } from "./-components/shift-details";
import { useIsMobile } from "@/utils/hooks/use-is-mobile";
import { useGetProfileQuery } from "@/queries/hooks/profileHooks";
import { SearchBar } from "../-dock/searchBar";
import z from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LoaderOverlay from "@/components/ui/loader-overlay";

/**
 * Adapt an employer worker-search result into the roster row shape so the Search
 * page can reuse the roster table. Roster-only columns (last worked / shifts /
 * next shift) come from the backend's roster-aware enrichment and render as "—"
 * for workers who haven't worked for this employer.
 */
function profileToRosterItem(p: Profile): RosterItem {
  return {
    worker_id: p.user_id,
    name: p.full_name ?? undefined,
    initials: undefined,
    roles: (p.preferred_roles ?? []).map((r) => getRoleLabelFromValue(r) ?? r),
    last_worked_at: p.last_worked_at ?? undefined,
    shift_count: p.prior_shift_count ?? 0,
    rating: p.overall_rating ?? p.rating ?? undefined,
    rating_count: p.rating_count ?? undefined,
    next_shift: p.next_shift ?? undefined,
    starred: p.starred ?? false,
    // The dedicated "Worked for you before" column carries this signal on Search,
    // so suppress the inline "New" badge that the roster uses.
    is_new: false,
    worked_before: p.worked_before ?? false,
    posted_availability: p.posted_availability ?? undefined,
  };
}


/** Compact "when" label for the Find-cover banner, e.g. "Tue, Oct 20 · 5:00 PM – 10:00 PM"
 *  (adds a date range when the shift spans multiple days). */
function formatCoverWhen(shift?: {
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
}): string | null {
  if (!shift?.start_date) return null;
  const fmtDate = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  const fmtTime = (tStr?: string | null) => {
    if (!tStr) return "";
    const [h, m] = tStr.split(":");
    const d = new Date();
    d.setHours(Number(h), Number(m) || 0, 0, 0);
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };
  const dateLabel =
    shift.end_date && shift.end_date !== shift.start_date
      ? `${fmtDate(shift.start_date)} – ${fmtDate(shift.end_date)}`
      : fmtDate(shift.start_date);
  const timeLabel =
    shift.start_time && shift.end_time
      ? `${fmtTime(shift.start_time)} – ${fmtTime(shift.end_time)}`
      : "";
  return [dateLabel, timeLabel].filter(Boolean).join(" · ");
}

// Ultimate fallback if the worker's profile has no saved location and the
// browser denies geolocation. Otherwise we prefer the worker's onboarded
// location (see `defaultCenter` inside SearchPage).
const FALLBACK_CENTER: google.maps.LatLngLiteral = {
  lat: 34.0522,
  lng: -118.2437,
};

const SearchParamsSchema = z.object({
  role_name: z.string().optional(),
  role_names: z.string().optional(),
  when: z.enum(["today", "weekend"]).optional(),
  lat: z.union([z.string(), z.number()]).optional(),
  lng: z.union([z.string(), z.number()]).optional(),
  radiusMiles: z.union([z.string(), z.number()]).optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  shiftDays: z.string().optional(),
  windows: z.string().optional(),
  requiresExperience: z.union([z.boolean(), z.string()]).optional(),
  // When set, the employer arrived via "Find cover" for this shift: pin it as context
  // and let them offer it to several workers at once (first-come-first-served).
  shiftId: z.coerce.number().optional(),
  // Why the shift is pinned: "cover" (replacing a worker) or "fill" (no sign-ups yet).
  // Only changes the banner wording; the offer flow is identical.
  intent: z.enum(["cover", "fill"]).optional(),
  // Employer Search table: server-side column sort + name/role text filter.
  sort: z.string().optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  q: z.string().optional(),
});

type SearchParams = z.infer<typeof SearchParamsSchema>;

export const Route = createFileRoute("/app/$role/_app/search/")({
  validateSearch: SearchParamsSchema,
  loaderDeps: ({
    search: { role_name, role_names, when, lat, lng, radiusMiles, windows },
  }) => ({
    role_name,
    role_names,
    when,
    lat,
    lng,
    radiusMiles,
    windows,
  }),

  loader: async ({ params, location, context }) => {
    const search: SearchParams = location.search;
    const role = params.role as Role;
    if (role === "employer") {
      await context.queryClient.ensureQueryData({
        queryKey: [QueryKeys.SHIFT_LIST],
        queryFn: () => getShifts({ page: 0, page_size: 50 }),
      });
    }
    return {
      queryKey: [QueryKeys.SEARCH, role, search],
      queryFn: async () => performSearch({ role, searchParams: search }),
    };
  },

  component: SearchPage,
  staticData: {
    layout: {
      boxed: false,
    },
  },
});

function SearchPage() {
  const { t } = useTranslation("search");
  // Simple debounce hook
  function useDebouncedCallback<T extends (...args: Array<any>) => void>(
    callback: T,
    delay: number,
  ) {
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const callbackRef = React.useRef(callback);
    React.useEffect(() => {
      callbackRef.current = callback;
    }, [callback]);
    return React.useCallback(
      (...args: Parameters<T>) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          callbackRef.current(...args);
        }, delay);
      },
      [delay],
    );
  }

  // Listen to search params from the router
  const searchParams = Route.useSearch();

  // Parse search params for SearchBar
  const parsedLat =
    typeof searchParams.lat === "string"
      ? parseFloat(searchParams.lat.replace(/"/g, ""))
      : searchParams.lat;
  const parsedLng =
    typeof searchParams.lng === "string"
      ? parseFloat(searchParams.lng.replace(/"/g, ""))
      : searchParams.lng;
  const parsedRadiusMiles =
    typeof searchParams.radiusMiles === "string"
      ? parseFloat(searchParams.radiusMiles.replace(/"/g, ""))
      : searchParams.radiusMiles;

  const navigate = useNavigate();
  const role = useRole();
  const profileQuery = useGetProfileQuery();
  const profileCenter = React.useMemo<google.maps.LatLngLiteral | null>(() => {
    const p = profileQuery.data?.profile;
    if (!p) return null;
    if (typeof p.latitude !== "number" || typeof p.longitude !== "number") {
      return null;
    }
    return { lat: p.latitude, lng: p.longitude };
  }, [profileQuery.data]);
  // The worker's onboarded location is the preferred starting point for the
  // map and the auto-search; LA is only used if the worker has no geocoded
  // profile yet.
  const defaultCenter = profileCenter ?? FALLBACK_CENTER;
  const { isFetching, data: searchData } = useQuery({
    queryKey: [QueryKeys.SEARCH, role, searchParams],
    queryFn: async () => performSearch({ role, searchParams }),
  });

  const initialShiftList: Array<Shift> = searchData?.shifts || [];
  const initialUserList: Array<Profile> = searchData?.users || [];
  const initialCenter: google.maps.LatLngLiteral =
    coerceCenter(searchData?.center) ?? defaultCenter;
  const initialMiles: number =
    typeof searchData?.miles === "number"
      ? searchData.miles
      : Number(searchData?.miles) || 3;
  const initialRoleName: string | undefined = searchData?.role_name;
  const initialWhen: "today" | "weekend" = searchData?.when as
    | "today"
    | "weekend";

  const [shifts, setShifts] = React.useState<Array<Shift>>(initialShiftList);
  const [miles, setMiles] = React.useState(initialMiles);
  const [selected, setSelected] = React.useState<Shift | Profile | null>(null);

  const [center, setCenter] =
    React.useState<google.maps.LatLngLiteral>(initialCenter);

  // initialCenter only runs once; if the profile query resolves after first
  // render and we're still parked on the hardcoded LA fallback, snap to the
  // worker's saved location.
  React.useEffect(() => {
    if (!profileCenter) return;
    setCenter((prev) => {
      if (prev.lat === FALLBACK_CENTER.lat && prev.lng === FALLBACK_CENTER.lng) {
        return profileCenter;
      }
      return prev;
    });
  }, [profileCenter]);

  // Refs to always get latest values in debounced callback
  const searchParamsRef = React.useRef(searchParams);
  const centerRef = React.useRef(center);
  const navigateRef = React.useRef(navigate);
  React.useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);
  React.useEffect(() => {
    centerRef.current = center;
  }, [center]);
  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Debounced navigate for miles change (hook at top level)
  const debouncedNavigate = useDebouncedCallback((newMiles: number) => {
    navigateRef.current({
      // `search` expects string params per route definition; cast to any to satisfy types
      search: {
        ...searchParamsRef.current,
        radiusMiles: newMiles.toString(),
        lat: centerRef.current.lat.toString(),
        lng: centerRef.current.lng.toString(),
      } as any,
      replace: false,
    });
  }, 300);

  const [users, setUsers] = React.useState<Array<Profile>>(initialUserList);
  const roleName = initialRoleName;
  const whenFilter = initialWhen;

  // Employer worker-search pagination. `total` is the full count of workers in
  // range (from the backend); `page` is the highest page we've loaded so far so
  // "Load more" can request the next one and append.
  const [total, setTotal] = React.useState<number | undefined>(
    searchData?.total,
  );
  const [page, setPage] = React.useState(0);
  const [loadingMore, setLoadingMore] = React.useState(false);

  // Employers have no map/search panel to trigger an imperative search, so the
  // initial (and param-driven) results from the query above never reached the
  // rendered `users` state — the page showed "No users found" despite results.
  // Sync the query's (first-page) users into state for employers. Guarded on
  // `page === 0` so a background refetch can't clobber already-loaded pages.
  React.useEffect(() => {
    if (role === "employer" && searchData && page === 0) {
      setUsers(searchData.users);
      setTotal(searchData.total);
    }
  }, [role, searchData, page]);

  const searchShiftsMutation = useSearchShiftsMutation();
  const searchWorkersMutation = useSearchWorkersMutation();

  const [loading, setLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  // Track last search params to avoid double-triggering on mount
  const lastSearchRef = React.useRef<any>(searchParams);

  // Listen for search param changes and trigger a new search
  // Extracted effect logic for search param changes
  const runImperativeSearch = React.useCallback(
    async (params: any) => {
      setLoading(true);
      setApiError(null);
      try {
        const result = await performSearch({
          role,
          searchParams: params,
          searchShiftsImpl: searchShiftsMutation.mutateAsync,
          searchWorkersImpl: searchWorkersMutation.mutateAsync,
        });
        setShifts(result.shifts);
        setUsers(result.users);
        setTotal(result.total);
        setPage(0);
        setCenter(result.center ?? defaultCenter);
      } catch (err: any) {
        Logger.error("Search failed", {
          err,
          message: err?.message,
          issues: err?.issues,
        });
        const message = err?.message ?? t("searchFailed");
        notify.error(t("searchFailed"), { description: String(message) });
        setApiError(String(message));
        setShifts([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [role, searchShiftsMutation, searchWorkersMutation, t],
  );

  React.useEffect(() => {
    if (
      lastSearchRef.current &&
      JSON.stringify(lastSearchRef.current) === JSON.stringify(searchParams)
    ) {
      return;
    }
    lastSearchRef.current = searchParams;
    runImperativeSearch(searchParams as any);
  }, [searchParams, runImperativeSearch]);

  const onSearch = React.useCallback(
    async (q: {
      center: google.maps.LatLngLiteral;
      radiusMiles: number;
      role_name?: string;
      startTime?: string;
      endTime?: string;
    }) => {
      const { center: searchCenter, radiusMiles, startTime, endTime } = q;
      setLoading(true);
      setApiError(null);

      const roleToUse = q.role_name ?? roleName;
      const payload: any = buildSearchPayload({
        center: searchCenter,
        radiusMiles: typeof radiusMiles === "number" ? radiusMiles : miles,
        role_names: roleToUse ? [roleToUse] : undefined,
        when: whenFilter,
        startTime,
        endTime,
        searchType: role === "worker" ? "shift" : "worker",
      });

      try {
        if (role === "worker") {
          const res = await searchShiftsMutation.mutateAsync(payload);
          const apiShifts = res.shifts;
          const uiShifts: Array<Shift> = apiShifts.map((s: any) =>
            mapApiShiftToUIShift(s),
          );
          setShifts(uiShifts);
          setCenter(computeCenterFromShifts(uiShifts));
        } else {
          const res = await searchWorkersMutation.mutateAsync(payload);
          setShifts([]);
          setUsers(res.users);
        }
      } catch (err: any) {
        Logger.error("Search failed", {
          err,
          message: err?.message,
          issues: err?.issues,
        });
        const message = err?.message ?? t("searchFailed");
        notify.error(t("searchFailed"), { description: String(message) });
        setApiError(String(message));
        if (role === "worker") {
          setShifts([]);
        } else {
          setUsers([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      role,
      roleName,
      whenFilter,
      searchShiftsMutation,
      searchWorkersMutation,
      miles,
      t,
    ],
  );

  // (Optional) You may want to disable the auto-search effect below if you want only search param changes to trigger search.
  const autoSearchedRef = React.useRef(false);
  React.useEffect(() => {
    // Auto geolocate + search when user navigates to /search with no query params
    if (autoSearchedRef.current) return;
    if (role === "employer") return; // employers don't search by location
    if (typeof window === "undefined") return;
    if (window.location.search && window.location.search.length > 1) return; // has explicit search
    // Wait for the profile query so we can prefer the worker's onboarded
    // location over (or in place of) a browser geolocation prompt.
    if (profileQuery.isLoading) return;

    autoSearchedRef.current = true;

    // Prefer the worker's saved profile location when present — no need to
    // bother them with a browser geolocation prompt.
    if (profileCenter) {
      setCenter(profileCenter);
      runImperativeSearch({
        ...searchParams,
        lat: profileCenter.lat.toString(),
        lng: profileCenter.lng.toString(),
        radiusMiles: miles.toString(),
      } as any);
      navigate({
        search: {
          ...searchParams,
          lat: profileCenter.lat.toString(),
          lng: profileCenter.lng.toString(),
          radiusMiles: miles.toString(),
        } as any,
        replace: false,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c);
        runImperativeSearch({
          ...searchParams,
          lat: c.lat.toString(),
          lng: c.lng.toString(),
          radiusMiles: miles.toString(),
        } as any);
        // Update URL search params to reflect geolocated search
        navigate({
          search: {
            ...searchParams,
            lat: c.lat.toString(),
            lng: c.lng.toString(),
            radiusMiles: miles.toString(),
          } as any,
          replace: false,
        });
      },
      (err) => {
        Logger.debug("Geolocation unavailable for auto-search", { err });
        // Fallback: use default center and update URL (FALLBACK_CENTER since
        // profileCenter is null on this branch).
        setCenter(defaultCenter);
        runImperativeSearch({
          ...searchParams,
          lat: defaultCenter.lat.toString(),
          lng: defaultCenter.lng.toString(),
          radiusMiles: miles.toString(),
        } as any);
        // Update URL search params
        navigate({
          search: {
            ...searchParams,
            lat: defaultCenter.lat.toString(),
            lng: defaultCenter.lng.toString(),
            radiusMiles: miles.toString(),
          } as any,
          replace: false,
        });
      },
      { enableHighAccuracy: true },
    );
    // If geolocation succeeds, also update URL search params
    // (move this logic into the success callback above)
  }, [
    miles,
    runImperativeSearch,
    navigate,
    searchParams,
    role,
    profileCenter,
    profileQuery.isLoading,
    defaultCenter,
  ]);

  function handleFind() {
    // Update the URL search params to match the current UI state
    navigate({
      search: {
        ...searchParams,
        lat: center.lat.toString(),
        lng: center.lng.toString(),
        radiusMiles: miles.toString(),
      } as any,
      replace: true,
    });
    // The effect will pick up the change and run the search
  }

  const isMobile = useIsMobile();
  const isEmployer = role === "employer";

  const handleMilesChange = React.useCallback(
    (newMiles: number) => {
      setMiles(newMiles);
      debouncedNavigate(newMiles);
    },
    [debouncedNavigate],
  );

  // Adapt search results into roster rows so the employer Search view renders
  // with the same table as the roster.
  const workerRows = React.useMemo<Array<RosterItem>>(
    () => users.map(profileToRosterItem),
    [users],
  );

  // Sort + filter for the employer Search table live in the URL (`sort`/`dir`/`q`)
  // and are applied by the backend, so they span the whole result set rather
  // than just the pages loaded so far. A param change re-runs the search from page 0.
  const sortKey = (searchParams.sort as RosterSortKey | undefined) ?? null;
  const sortDir = searchParams.dir ?? (sortKey ? ROSTER_SORT_DEFAULT_DIR[sortKey] : "asc");
  const handleSort = React.useCallback(
    (key: RosterSortKey) => {
      const current = searchParamsRef.current;
      const nextDir =
        current.sort === key
          ? (current.dir ?? ROSTER_SORT_DEFAULT_DIR[key]) === "asc"
            ? "desc"
            : "asc"
          : ROSTER_SORT_DEFAULT_DIR[key];
      navigate({
        search: { ...current, sort: key, dir: nextDir } as never,
        replace: true,
      });
    },
    [navigate],
  );
  const [rosterFilter, setRosterFilter] = React.useState(searchParams.q ?? "");
  // Follow external URL changes (back/forward) without clobbering in-progress
  // typing, e.g. a trailing space the debounced navigate trimmed off.
  React.useEffect(() => {
    const q = searchParams.q ?? "";
    setRosterFilter((prev) => (prev.trim() === q ? prev : q));
  }, [searchParams.q]);
  const debouncedFilterNavigate = useDebouncedCallback((value: string) => {
    const q = value.trim() || undefined;
    if (q === (searchParamsRef.current.q || undefined)) return;
    navigateRef.current({
      search: { ...searchParamsRef.current, q } as never,
      replace: true,
    });
  }, 300);
  const filterActive = Boolean(searchParams.q);

  const queryClient = useQueryClient();
  const handleToggleStar = React.useCallback(
    async (workerId: string, nextStarred: boolean) => {
      // Optimistically flip the local row; the roster table isn't backed by the
      // roster query cache here, so we own the state.
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === workerId ? { ...u, starred: nextStarred } : u,
        ),
      );
      try {
        await setRosterStar(workerId, nextStarred);
        // Keep the roster page consistent next time it's opened.
        queryClient.invalidateQueries({ queryKey: [QueryKeys.ROSTER] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.STARRED_WORKERS] });
      } catch (err: any) {
        setUsers((prev) =>
          prev.map((u) =>
            u.user_id === workerId ? { ...u, starred: !nextStarred } : u,
          ),
        );
        notify.error(t("searchFailed"), {
          description: String(err?.message ?? err),
        });
      }
    },
    [queryClient, t],
  );

  // "Find cover": the employer arrived from a specific shift (?shiftId). Pin that shift as
  // context and let them offer it to several selected workers at once. Offers go out as
  // ACCEPTED; the first worker to confirm wins (first-come-first-served, enforced by the API).
  const coverShiftId =
    typeof searchParams.shiftId === "number" ? searchParams.shiftId : undefined;
  const coverMode = isEmployer && coverShiftId != null;
  // Load the pinned shift directly (it may be older than any list page); the detail also
  // carries the other days of a multi-day series for the day picker below the banner.
  const coverShiftQuery = useQuery({
    queryKey: [QueryKeys.UPCOMING_SHIFTS, "detail", "employer", coverShiftId],
    queryFn: () => getShiftDetail(coverShiftId!),
    enabled: coverMode,
  });
  const coverShift = coverMode ? coverShiftQuery.data : undefined;
  // Days of the series to offer (just the pinned shift when it isn't part of a series).
  // null until the picker has loaded; after that it's exactly what's ticked (may be empty
  // when every day is already filled — then nothing can be offered).
  const [coverDayIds, setCoverDayIds] = React.useState<Array<number> | null>(null);
  const coverDays = React.useMemo(
    () => (coverShiftId == null ? [] : (coverDayIds ?? [coverShiftId])),
    [coverShiftId, coverDayIds],
  );
  const [selectedWorkerIds, setSelectedWorkerIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const toggleSelectWorker = React.useCallback((workerId: string) => {
    setSelectedWorkerIds((prev) => {
      const next = new Set(prev);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  }, []);
  const offerMutation = useAddUpdateOfferMutation();
  const [offeringCover, setOfferingCover] = React.useState(false);
  const handleOfferCoverToSelected = React.useCallback(async () => {
    if (coverShiftId == null || selectedWorkerIds.size === 0 || coverDays.length === 0) return;
    const ids = Array.from(selectedWorkerIds);
    setOfferingCover(true);
    try {
      // One offer per worker per chosen day (each day of a series is its own shift).
      const results = await Promise.allSettled(
        ids.flatMap((userId) =>
          coverDays.map((shiftId) =>
            offerMutation.mutateAsync({ userId, shiftId, offerStatus: "ACCEPTED" }),
          ),
        ),
      );
      const offerOk = (r: PromiseSettledResult<unknown>) =>
        r.status === "fulfilled" && (r.value as { success?: boolean }).success !== false;
      // A worker counts as offered when at least one of their days went out.
      const ok = ids.filter((_, i) =>
        results.slice(i * coverDays.length, (i + 1) * coverDays.length).some(offerOk),
      ).length;
      const failed = ids.length - ok;
      if (ok > 0) {
        notify.success(
          coverDays.length > 1
            ? t("cover.offersSentDays", "Offered {{days}} days to {{count}} worker(s)", {
                days: coverDays.length,
                count: ok,
              })
            : t("cover.offersSent", "Offered the shift to {{count}} worker(s)", { count: ok }),
        );
      }
      if (failed > 0) {
        // Surface the server's reason (e.g. the worker already declined) when there is one.
        const reason = results
          .map((r) =>
            r.status === "rejected"
              ? (r.reason as Error | undefined)?.message
              : (r.value as { message?: string }).message,
          )
          .find(Boolean);
        const summary = t("cover.offersFailed", "Couldn't offer to {{count}} worker(s)", {
          count: failed,
        });
        notify.error(reason ? `${summary}: ${reason}` : summary);
      }
      setSelectedWorkerIds(new Set());
    } finally {
      setOfferingCover(false);
    }
  }, [coverShiftId, coverDays, selectedWorkerIds, offerMutation, t]);

  const canLoadMore =
    typeof total === "number" && users.length < total && !loadingMore;

  const handleLoadMore = React.useCallback(async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const result = await performSearch({
        role,
        searchParams,
        page: nextPage,
        searchShiftsImpl: searchShiftsMutation.mutateAsync,
        searchWorkersImpl: searchWorkersMutation.mutateAsync,
      });
      // Append the next page, de-duping by user_id so a shifting result set
      // (e.g. a worker added between page fetches) can't surface a row twice.
      setUsers((prev) => {
        const seen = new Set(prev.map((u) => u.user_id));
        return [...prev, ...result.users.filter((u) => !seen.has(u.user_id))];
      });
      setPage(nextPage);
      if (typeof result.total === "number") setTotal(result.total);
    } catch (err: any) {
      Logger.error("Load more workers failed", { err, message: err?.message });
      notify.error(t("searchFailed"), {
        description: String(err?.message ?? err),
      });
    } finally {
      setLoadingMore(false);
    }
  }, [page, role, searchParams, searchShiftsMutation, searchWorkersMutation, t]);

  // Effective radius shown in the header: the employer's chosen radius if the
  // URL carries one, otherwise the 50-mile default the payload falls back to.
  const employerRadiusMiles =
    typeof parsedRadiusMiles === "number" && !isNaN(parsedRadiusMiles)
      ? parsedRadiusMiles
      : EMPLOYER_SEARCH_RADIUS_MILES;

  return (
    <>
      {isMobile && (
        <div
          className="absolute left-2 right-2 z-50 pointer-events-auto w-auto"
          style={{ top: "calc(var(--app-header-h, 80px) + 8px)" }} // header + 8px margin
        >
          <SearchBar
            initialRoles={(() => {
              if (typeof searchParams.role_names === "string" && searchParams.role_names.length)
                return searchParams.role_names.split(",").filter(Boolean);
              return searchParams.role_name ? [searchParams.role_name] : [];
            })()}
            initialWindows={(() => {
              if (typeof searchParams.windows !== "string") return undefined;
              try {
                return JSON.parse(searchParams.windows);
              } catch {
                return undefined;
              }
            })()}
            initialLat={parsedLat}
            initialLng={parsedLng}
            initialRadiusMiles={parsedRadiusMiles}
            initialRequiresExperience={
              searchParams.requiresExperience === true ||
              searchParams.requiresExperience === "true"
            }
          />
        </div>
      )}
      {isEmployer ? (
        <div className="relative" style={{ minHeight: "100vh" }}>
          <div className="w-full max-w-[1200px] mx-auto px-3 sm:px-6 md:px-8 pt-24 md:pt-8 pb-16">
            <div className="flex items-baseline gap-3 mb-1 flex-wrap">
              <h1 className="heading-1">{t("workersTitle")}</h1>
              {typeof total === "number" && workerRows.length ? (
                <span className="text-sm text-slate">
                  {t("workersShownOfTotal", {
                    shown: workerRows.length,
                    total,
                  })}
                </span>
              ) : workerRows.length ? (
                <span className="text-sm text-slate">
                  {t("workersCount", { count: workerRows.length })}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-slate mb-4">
              {t("workersWithinRadius", { miles: employerRadiusMiles })}
            </p>
            {coverMode ? (
              <div className="mb-4 rounded-[12px] border border-warn-border bg-warn-tint px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-garnet truncate">
                    {searchParams.intent === "fill"
                      ? t("cover.bannerFill", "Finding workers for")
                      : t("cover.banner", "Finding cover for")}{" "}
                    {coverShift?.name ?? t("cover.thisShift", "this shift")}
                  </div>
                  <div className="text-xs text-slate">
                    {coverShift
                      ? [
                          getRoleLabelFromValue(coverShift.role_name ?? "") ??
                            coverShift.role_name,
                          formatCoverWhen(coverShift),
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : t("cover.selectHint", "Select workers to offer this shift to")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    navigate({
                      search: { ...searchParams, shiftId: undefined, intent: undefined } as never,
                      replace: true,
                    })
                  }
                  className="shrink-0 text-xs text-slate hover:text-garnet underline"
                >
                  {t("cover.exit", "Exit")}
                </button>
              </div>
            ) : null}
            {coverShiftId != null ? (
              <div className="mb-4 -mt-2 rounded-[12px] bg-white px-4 empty:hidden">
                <OfferDaysPicker shiftId={coverShiftId} onChange={setCoverDayIds} />
              </div>
            ) : null}
            <section className="bg-white rounded-[15px] p-4 sm:p-5">
              {workerRows.length > 0 || filterActive ? (
                <div className="mb-3">
                  <input
                    type="search"
                    value={rosterFilter}
                    onChange={(e) => {
                      setRosterFilter(e.target.value);
                      debouncedFilterNavigate(e.target.value);
                    }}
                    placeholder={t("filterPlaceholder", "Filter by name or role")}
                    className="h-9 w-full sm:w-72 rounded-lg border border-fog-dark px-3 text-sm text-garnet placeholder:text-slate/60 focus-visible:outline-2 focus-visible:outline-teal"
                  />
                </div>
              ) : null}
              {workerRows.length > 0 ? (
                <>
                  <RosterItemsTable
                    items={workerRows}
                    filter="all"
                    showWorkedBefore
                    onToggleStar={handleToggleStar}
                    selectedWorkerIds={coverMode ? selectedWorkerIds : undefined}
                    onToggleSelect={coverMode ? toggleSelectWorker : undefined}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  {canLoadMore || loadingMore ? (
                    <div className="pt-4 flex justify-center">
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="h-10 px-5 rounded-lg border border-fog-dark text-sm text-garnet hover:bg-fog disabled:opacity-50"
                      >
                        {loadingMore ? t("loadingMore") : t("loadMore")}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : filterActive ? (
                !loading &&
                !isFetching && (
                  <p className="text-sm text-slate py-8 text-center">
                    {t("noWorkersMatchFilter", "No workers match your filter.")}
                  </p>
                )
              ) : (
                !loading &&
                !isFetching && (
                  <p className="text-sm text-slate py-8 text-center">
                    {t("noUsersFound")}
                  </p>
                )
              )}
            </section>
          </div>
          {isFetching && <LoaderOverlay />}
        </div>
      ) : (
        <div className="relative lg:min-h-screen">
          <ShiftMap
            initialCenter={center}
            center={center}
            radiusMiles={miles}
            initialZoom={12}
            shifts={shifts}
            users={users}
            onSearch={onSearch}
            onMarkerClick={(item) => {
              setSelected(item);
            }}
          />
          {isFetching && <LoaderOverlay />}
        </div>
      )}
      {/* Results panel and overlays only */}
      {loading && (
        <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
          <LoaderOverlay />
        </div>
      )}
      {apiError && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-red-100 text-red-800 rounded-md px-3 py-2 text-sm">
            {apiError}
          </div>
        </div>
      )}
      {!loading && !isEmployer && shifts.length === 0 && (
        <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-white/80 rounded-md p-3 text-sm text-gray-700">
            {t("noResultsFound")}
          </div>
        </div>
      )}
      {selected && "id" in selected ? (
        <ShiftDetails
          shift={selected as Shift}
          role={role}
          onClose={() => setSelected(null)}
          onInterested={(id) => {
            Logger.debug("Interested click", { id });
          }}
          onMessage={(shiftId) => {
            const shift = shifts.find((item) => item.id == shiftId.toString());
            if (shift && shift.business) {
              const recipientId = shift.business.user_id;
              navigate({
                to: "/app/$role/messages",
                params: { role },
                search: {
                  tab: "messages",
                  to: recipientId,
                  businessId: undefined,
                  selectedOfferId: undefined,
                },
              });
            }
          }}
        />
      ) : selected && "user_id" in selected ? (
        <UserDetails
          open={true}
          user={selected}
          onClose={() => setSelected(null)}
          onSendOffer={(userId) => {
            Logger.debug("Send Offer", { userId });
          }}
          onSendMessage={(userId) => {
            Logger.debug("Send Message", { userId });
          }}
        />
      ) : null}

      {coverMode && selectedWorkerIds.size > 0 ? (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-fog-dark px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-garnet font-medium">
            {t("cover.selectedCount", "{{count}} selected", {
              count: selectedWorkerIds.size,
            })}
          </span>
          <button
            type="button"
            onClick={handleOfferCoverToSelected}
            disabled={offeringCover || coverDays.length === 0}
            className="h-10 px-5 rounded-lg bg-garnet text-white text-sm font-semibold disabled:opacity-50"
          >
            {offeringCover
              ? t("cover.sending", "Sending…")
              : coverDays.length === 0
                ? t("cover.noOpenDays", "No open days selected")
                : coverDays.length > 1
                ? t("cover.offerDaysButton", "Offer {{days}} days to {{count}} worker(s)", {
                    days: coverDays.length,
                    count: selectedWorkerIds.size,
                  })
                : t("cover.offerButton", "Offer shift to {{count}} worker(s)", {
                    count: selectedWorkerIds.size,
                  })}
          </button>
        </div>
      ) : null}

      {!isEmployer && (
        <>
          <FindShiftPanel
            miles={miles}
            onMilesChange={handleMilesChange}
            onFind={handleFind}
            role={role}
          />
          <BottomViewToggle
            onListClick={() =>
              navigate({
                to: "/app/$role/search/list",
                params: { role },
                search: searchParams,
              })
            }
          />
        </>
      )}
    </>
  );
}
